// Reversals: take back rewards a network rejected when it doesn't send a reversal postback (it lists
// them in its dashboard, or sends a CSV or spreadsheet). Staff pick the network, paste the ids or
// upload the report (read here in the browser, never uploaded), see exactly what would happen, then
// apply. The API runs each one through the same take-back as a network's reversal postback, so
// nothing is taken twice, and the History tab keeps every batch with each id's outcome.
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, History as HistoryIcon, TriangleAlert, Undo2, X } from 'lucide-react';
import {
  columnIds,
  columnLetter,
  guessIdColumn,
  idListError,
  looksLikeTable,
  MAX_REVERSAL_IDS,
  parseDelimited,
  parseIdText,
  type IdList,
} from '@gaintplay/core/reversal-input';
import type { XlsxSheet } from '@gaintplay/core/xlsx';
import { api, ApiError } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  Card,
  confirmAction,
  Drawer,
  EmptyState,
  ErrorNote,
  fmtNum,
  fmtUsd,
  KV,
  Mono,
  PageHeader,
  Pill,
  run,
  ScrollX,
  SkeletonRows,
  StatusPill,
  Tabs,
  TimeAgo,
  UserChip,
} from '../components/ui';

/* ------------------------------------------------------------------ Types */

interface Network {
  id: string;
  name: string;
  status: string;
}
interface Batch {
  id: string;
  providerId: string;
  network: string | null;
  staffId: string;
  staffName: string | null;
  reason: string;
  status: 'applying' | 'done';
  total: number;
  reversed: number;
  alreadyReversed: number;
  waiting: number;
  notFound: number;
  failed: number;
  coins: number;
  revenueMinor: number;
  commissionCoins: number;
  shortfallCoins: number;
  createdAt: number;
  finishedAt: number | null;
}
interface HistoryPage {
  batches: Batch[];
  next: string | null;
  networks: Network[];
  maxIds: number;
}
interface PreviewItem {
  txid: string;
  state: 'credited' | 'held' | 'reversed' | 'waiting' | 'not_found';
  conversionId: string | null;
  userId: string | null;
  username: string | null;
  userStatus: string | null;
  offerName: string | null;
  coins: number;
  revenueMinor: number | null;
  createdAt: number | null;
  reversedBy: 'network' | 'staff' | null;
  reversedAt: number | null;
  commission: { userId: string; username: string | null; coins: number; held: boolean } | null;
  shortfall: number;
  commissionShortfall: number;
}
interface ShortUser {
  userId: string;
  username: string | null;
  status: string;
  available: number;
  pending: number;
  withdrawing: number;
  takeBack: number;
  short: number;
  flagged: boolean;
  unsentCashouts: number;
}
interface Preview {
  providerId: string;
  items: PreviewItem[];
  totals: {
    ids: number;
    toReverse: number;
    credited: number;
    held: number;
    alreadyReversed: number;
    waiting: number;
    notFound: number;
    coins: number;
    revenueMinor: number;
    commissionCoins: number;
    shortfallCoins: number;
  };
  shortUsers: ShortUser[];
}
interface BatchItem {
  txid: string;
  outcome: 'pending' | 'waiting' | 'reversed' | 'already_reversed' | 'not_found' | 'failed';
  coins: number | null;
  wasHeld: boolean | null;
  commissionCoins: number | null;
  shortfall: number | null;
  note: string | null;
  actor: string | null;
  processedAt: number | null;
  conversionId: string | null;
  userId: string | null;
  username: string | null;
  userStatus: string | null;
  offerName: string | null;
  conversionCoins: number | null;
  convertedAt: number | null;
}

/* ------------------------------------------------------------------ Small helpers */

const day = (t: number | null) =>
  t
    ? new Date(t).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
const cents = (n: number) => fmtUsd(n / 100);
const plural = (n: number, one: string, many = `${one}s`) => `${fmtNum(n)} ${n === 1 ? one : many}`;
/** Coins leaving a balance: berry, tabular, with a real minus sign. */
function Minus({ coins, className = '' }: { coins: number; className?: string }) {
  return (
    <span className={`font-bold tabular-nums text-berry-deep ${className}`}>
      {coins ? `−${fmtNum(coins)}` : '0'}
    </span>
  );
}

/** Rewards this preview would take back come from how many accounts. */
const usersIn = (p: Preview) =>
  new Set(p.items.filter((i) => i.state === 'credited' || i.state === 'held').map((i) => i.userId))
    .size;

function useHistory(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['reversals'],
    queryFn: ({ pageParam }) =>
      api<HistoryPage>(`/reversals${pageParam ? `?before=${pageParam}` : ''}`),
    initialPageParam: null as string | null,
    getNextPageParam: (p) => p.next,
    enabled,
  });
}
type HistoryQuery = ReturnType<typeof useHistory>;

/* ------------------------------------------------------------------ Page */

export function Reversals() {
  const can = useCan();
  const canApply = can('reversals.apply');
  const canRead = canApply || can('postbacks.view');
  const [params, setParams] = useSearchParams();
  const tab = !canApply || params.get('tab') === 'history' ? 'history' : 'new';
  const history = useHistory(canRead);
  const first = history.data?.pages[0];
  const unfinished = (history.data?.pages ?? [])
    .flatMap((p) => p.batches)
    .filter((b) => b.status === 'applying').length;

  if (!canRead)
    return (
      <div className="space-y-5">
        <PageHeader title="Reversals" />
        <div className="card">
          <EmptyState icon={Undo2} title="Reversals aren’t part of your role">
            Ask an owner for “Take back rewards a network reversed” or “See the postback log”.
          </EmptyState>
        </div>
      </div>
    );

  const setTab = (t: string) =>
    setParams((p) => (t === 'history' ? p.set('tab', 'history') : p.delete('tab'), p), {
      replace: true,
    });
  return (
    <div className="space-y-5">
      <PageHeader
        title="Reversals"
        description={
          <>
            Take back rewards a network rejected when it doesn’t send a reversal postback. Each one
            runs the same take-back as a network’s reversal, so a reward is never taken twice, even
            if the network’s postback arrives later. Up to{' '}
            {fmtNum(first?.maxIds ?? MAX_REVERSAL_IDS)} transaction ids per batch.
          </>
        }
      />
      {canApply && (
        <Tabs
          label="Reversals"
          value={tab}
          onChange={setTab}
          items={[
            { key: 'new', label: 'Take back' },
            { key: 'history', label: 'History', count: unfinished, hot: true },
          ]}
        />
      )}
      {/* Kept mounted behind History, so a list being prepared or applied isn't lost. */}
      {canApply && (
        <div hidden={tab !== 'new'}>
          <TakeBack
            networks={first?.networks}
            onHistory={(id) => setParams({ tab: 'history', batch: id })}
          />
        </div>
      )}
      {tab === 'history' && <HistoryList query={history} canApply={canApply} />}
    </div>
  );
}

/* ------------------------------------------------------------------ Take back: input */

type Source = 'paste' | 'file';

/** A report as the column picker needs it; an .xlsx keeps its bytes to switch sheets. */
interface Report {
  name: string;
  rows: string[][];
  xlsx: { bytes: Uint8Array; sheets: XlsxSheet[]; sheet: number } | null;
}

/** The .xlsx reader is loaded only when someone picks a workbook. */
async function readXlsxSheet(bytes: Uint8Array, sheet?: number) {
  const { readXlsx } = await import('@gaintplay/core/xlsx');
  return readXlsx(bytes, sheet);
}

/** A report file as rows: .xlsx through the lazily loaded reader, anything else as CSV/TSV text. */
async function readReport(file: File): Promise<Report> {
  if (file.size > 10 * 1024 * 1024)
    throw new Error('That file is over 10 MB. Export only the rejected leads.');
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes[0] === 0x50 && bytes[1] === 0x4b) {
    const r = await readXlsxSheet(bytes);
    return { name: file.name, rows: r.rows, xlsx: { bytes, sheets: r.sheets, sheet: r.sheet } };
  }
  if (bytes[0] === 0xd0 && bytes[1] === 0xcf)
    throw new Error(
      'This is an old Excel file (.xls). Save it as .xlsx or CSV and choose it again.',
    );
  const text =
    bytes[0] === 0xff && bytes[1] === 0xfe
      ? new TextDecoder('utf-16le').decode(bytes)
      : new TextDecoder('utf-8').decode(bytes);
  return { name: file.name, rows: parseDelimited(text), xlsx: null };
}

function TakeBack({
  networks,
  onHistory,
}: {
  networks: Network[] | undefined;
  onHistory: (batchId: string) => void;
}) {
  const ids = useId();
  const [providerId, setProviderId] = useState('');
  const [source, setSource] = useState<Source>('paste');
  const [text, setText] = useState('');
  /** Staff said a paste that looks like a table is a plain list after all. */
  const [asList, setAsList] = useState(false);
  const [fromTable, setFromTable] = useState<IdList | null>(null);
  const [fromFile, setFromFile] = useState<IdList | null>(null);
  const [preview, setPreview] = useState<{ key: string; data: Preview } | null>(null);
  const [previewError, setPreviewError] = useState<unknown>(null);
  const [batch, setBatch] = useState<Batch | null>(null);

  const isTable = useMemo(() => looksLikeTable(text), [text]);
  const table = useMemo(
    () => (isTable && !asList ? parseDelimited(text) : null),
    [text, isTable, asList],
  );
  const pasted = useMemo(() => parseIdText(text), [text]);
  const list: IdList | null =
    source === 'paste' ? (!text.trim() ? null : table ? fromTable : pasted) : fromFile;
  const count = list?.ids.length ?? 0;
  const limit = count > MAX_REVERSAL_IDS ? idListError(count) : null;
  const key = `${providerId}\n${list?.ids.join('\n') ?? ''}`;
  const current = preview?.key === key ? preview.data : null;
  const network = networks?.find((n) => n.id === providerId);

  const runPreview = async () => {
    setPreviewError(null);
    try {
      const data = await api<Preview>('/reversals/preview', {
        body: { providerId, txids: list!.ids },
      });
      setPreview({ key, data });
    } catch (e) {
      setPreviewError(e);
    }
  };
  const reset = () => {
    setBatch(null);
    setPreview(null);
    setText('');
    setAsList(false);
    setFromTable(null);
    setFromFile(null);
  };

  if (batch)
    return (
      <Progress
        batch={batch}
        onChange={setBatch}
        done={
          <>
            <button type="button" className="btn-primary" onClick={reset}>
              Start another batch
            </button>
            <button type="button" className="btn-ghost" onClick={() => onHistory(batch.id)}>
              See every id’s outcome
            </button>
          </>
        }
      />
    );

  return (
    <div className="space-y-5">
      <Card title="1 · Network and transaction ids">
        <div className="space-y-5">
          <div className="max-w-sm">
            <label className="label" htmlFor={`${ids}-net`}>
              Network
            </label>
            <select
              id={`${ids}-net`}
              className="input h-11 py-0"
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
            >
              <option value="">Choose the network that rejected them</option>
              {networks?.map((n) => (
                <option key={n.id} value={n.id}>
                  {n.name}
                  {n.status !== 'active' ? ` (${n.status})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Tabs
              label="Where the ids come from"
              value={source}
              onChange={setSource}
              items={[
                { key: 'paste', label: 'Paste ids' },
                { key: 'file', label: 'Upload a report' },
              ]}
            />
            {/* Both stay mounted, so switching tabs keeps what each one holds. */}
            <div className="mt-3" hidden={source !== 'paste'}>
              <label className="label" htmlFor={`${ids}-paste`}>
                Transaction ids
              </label>
              <textarea
                id={`${ids}-paste`}
                className="input min-h-[168px] resize-y font-mono text-[13px] leading-relaxed"
                placeholder={
                  'One per line, or separated by commas or spaces\nTX-88412\nTX-88419\n\nA table copied from a spreadsheet works too: you pick its id column.'
                }
                spellCheck={false}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setAsList(false);
                }}
              />
              {table ? (
                <div className="mt-3 space-y-3">
                  <p className="text-[13px] leading-snug text-graphite">
                    This looks like a table, so not every value is a transaction id.{' '}
                    <button
                      type="button"
                      className="font-semibold text-ink underline decoration-ink/25 underline-offset-2 hover:decoration-ink"
                      onClick={() => setAsList(true)}
                    >
                      It’s a plain list: use every value
                    </button>
                  </p>
                  <ColumnPicker rows={table} providerId={providerId} onIds={setFromTable} />
                </div>
              ) : (
                <>
                  <IdCount list={text.trim() ? pasted : null} />
                  {isTable && (
                    <button
                      type="button"
                      className="mt-1 text-[12px] font-semibold text-ink underline decoration-ink/25 underline-offset-2 hover:decoration-ink"
                      onClick={() => setAsList(false)}
                    >
                      Pick one column instead
                    </button>
                  )}
                </>
              )}
            </div>
            <div className="mt-3" hidden={source !== 'file'}>
              <ReportFile providerId={providerId} onIds={setFromFile} />
            </div>
          </div>
          {limit && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-[12px] border border-berry/30 bg-berry/[0.06] px-4 py-3 text-[14px] font-semibold text-berry-deep"
            >
              <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" /> {limit}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-4">
            <button
              type="button"
              className="btn-primary"
              disabled={!providerId || !count || !!limit}
              onClick={runPreview}
            >
              {count ? `Preview ${plural(count, 'id')}` : 'Preview'}
            </button>
            <span className="text-[13px] text-stone">
              {!providerId
                ? 'Choose a network first.'
                : !count
                  ? 'Add the transaction ids the network rejected.'
                  : 'Nothing changes until you apply.'}
            </span>
          </div>
          <ErrorNote error={previewError} onRetry={runPreview} />
        </div>
      </Card>
      {current && (
        <>
          <PreviewView preview={current} network={network?.name ?? providerId} />
          {current.totals.toReverse + current.totals.waiting > 0 && (
            <ApplyPanel
              preview={current}
              network={network?.name ?? providerId}
              onStarted={setBatch}
            />
          )}
        </>
      )}
    </div>
  );
}

/** "312 ids · 4 repeats removed · 1 cut to 200 characters", under the input. */
function IdCount({ list, from }: { list: IdList | null; from?: string }) {
  if (!list)
    return <p className="mt-1.5 text-[12px] text-stone">Blank lines and repeats are ignored.</p>;
  const parts = [
    `${plural(list.ids.length, 'id')}${from ? ` ${from}` : ''}`,
    list.duplicates ? `${plural(list.duplicates, 'repeat')} removed` : '',
    list.truncated
      ? `${plural(list.truncated, 'id')} cut to 200 characters, as postbacks store them`
      : '',
  ].filter(Boolean);
  return (
    <p className="mt-1.5 text-[12px] tabular-nums text-graphite" aria-live="polite">
      {parts.join(' · ')}
    </p>
  );
}

/** Columns the picker shows: those with anything in the first rows, at most this many. */
const SHOWN_COLUMNS = 60;
/** Ids per column checked against the network (the API's limit too). */
const MATCH_SAMPLE = 200;

/**
 * A table's first rows, to click the column that holds the transaction ids. The guess comes from
 * the header (a transaction id first, never a user, offer or amount column); with a network chosen,
 * each column shows how many of its ids that network knows, so a wrong column stands out.
 */
function ColumnPicker({
  rows,
  providerId,
  onIds,
}: {
  rows: string[][];
  providerId: string;
  onIds: (l: IdList | null) => void;
}) {
  const ids = useId();
  const guess = useMemo(() => guessIdColumn(rows), [rows]);
  const [column, setColumn] = useState<number | null>(guess.column);
  const [header, setHeader] = useState(guess.header);
  useEffect(() => {
    setColumn(guess.column);
    setHeader(guess.header);
  }, [guess]);
  const list = useMemo(
    () => (column === null ? null : columnIds(rows, column, header)),
    [rows, column, header],
  );
  useEffect(() => {
    onIds(list);
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps

  const cols = useMemo(() => {
    const sample = rows.slice(0, 50);
    const width = Math.max(0, ...sample.map((r) => r.length));
    const out: number[] = [];
    for (let c = 0; c < width && out.length < SHOWN_COLUMNS; c++)
      if (sample.some((r) => (r[c] ?? '').trim())) out.push(c);
    return out;
  }, [rows]);
  const samples = useMemo(
    () => cols.map((c) => columnIds(rows, c, header).ids.slice(0, MATCH_SAMPLE)),
    [rows, cols, header],
  );
  // A fresh key per table, rather than the rows themselves in the query key.
  const tableKey = useMemo(() => Math.random().toString(36).slice(2), [rows]);
  const matches = useQuery({
    queryKey: ['reversals-match', providerId, tableKey, header],
    queryFn: () =>
      api<{ found: number[] }>('/reversals/match', { body: { providerId, columns: samples } }),
    enabled: !!providerId && cols.length > 0,
    staleTime: 5 * 60_000,
  });
  const found = matches.data?.found;
  const at = column === null ? -1 : cols.indexOf(column);
  // Another column the network knows much better than the chosen one.
  const better =
    found && found.length === cols.length
      ? cols
          .map((c, i) => ({ c, n: found[i]!, of: samples[i]!.length }))
          .filter((x) => x.c !== column && x.n > 0 && x.n > (at >= 0 ? found[at]! : 0) * 2)
          .sort((a, b) => b.n - a.n)[0]
      : undefined;
  const label = (c: number) =>
    `${columnLetter(c)}${header && rows[0]?.[c] ? ` (${rows[0][c]})` : ''}`;

  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-5 gap-y-2">
        <span id={`${ids}-cols`} className="text-[13px] font-semibold text-graphite">
          {column === null
            ? 'Click the column that holds the transaction ids.'
            : 'Which column holds the transaction id? Click another to change it.'}
        </span>
        <label className="flex items-center gap-2 text-[13px] font-semibold text-graphite">
          <input
            type="checkbox"
            className="h-4 w-4 accent-ink"
            checked={header}
            onChange={(e) => setHeader(e.target.checked)}
          />
          First row is a header
        </label>
      </div>
      <div className="overflow-hidden rounded-[12px] border border-hairline bg-paper">
        <ScrollX>
          <table className="w-full text-[13px]" aria-describedby={`${ids}-cols`}>
            <thead>
              <tr>
                {cols.map((c, i) => (
                  <th key={c} scope="col" className="border-b border-hairline p-1 align-top">
                    <button
                      type="button"
                      aria-pressed={column === c}
                      onClick={() => setColumn(c)}
                      className={`flex min-h-8 w-full min-w-[104px] flex-col items-start rounded-[8px] px-2.5 py-1.5 text-left text-[12px] font-semibold transition ${
                        column === c ? 'bg-ink text-white' : 'text-graphite hover:bg-canvas'
                      }`}
                    >
                      <span className="flex max-w-full items-center gap-1.5">
                        <span className="tabular-nums">{columnLetter(c)}</span>
                        {header && rows[0]?.[c] && (
                          <span className="truncate font-normal opacity-80">· {rows[0][c]}</span>
                        )}
                      </span>
                      {found?.[i] !== undefined && (
                        <span className="text-[11px] font-normal tabular-nums opacity-80">
                          {fmtNum(found[i]!)} of {fmtNum(samples[i]!.length)} known
                        </span>
                      )}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(header ? 1 : 0, (header ? 1 : 0) + 5).map((r, ri) => (
                <tr key={ri} className="border-b border-hairline last:border-b-0">
                  {cols.map((c) => (
                    <td
                      key={c}
                      onClick={() => setColumn(c)}
                      className={`max-w-[220px] cursor-pointer truncate px-3.5 py-2 ${
                        column === c
                          ? 'bg-canvas font-mono font-semibold text-ink'
                          : 'text-graphite'
                      }`}
                    >
                      {r[c] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      </div>
      {list ? (
        <IdCount list={list} from={`from column ${label(column!)}`} />
      ) : (
        <p className="mt-1.5 text-[12px] font-semibold text-berry-deep">No column chosen yet.</p>
      )}
      {found && at >= 0 && found[at] === 0 && samples[at]!.length > 0 && !better && (
        <p className="mt-1.5 text-[13px] font-semibold text-berry-deep">
          None of the first {fmtNum(samples[at]!.length)} ids in this column are this network’s.
          Check the network and the column.
        </p>
      )}
      {better && (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-semibold text-berry-deep">
          <TriangleAlert size={15} className="shrink-0" aria-hidden="true" />
          Column {label(better.c)} matches more of this network’s transactions ({fmtNum(better.n)}{' '}
          of {fmtNum(better.of)}).
          <button
            type="button"
            className="text-ink underline decoration-ink/25 underline-offset-2 hover:decoration-ink"
            onClick={() => setColumn(better.c)}
          >
            Use column {columnLetter(better.c)}
          </button>
        </p>
      )}
    </div>
  );
}

function ReportFile({
  providerId,
  onIds,
}: {
  providerId: string;
  onIds: (l: IdList | null) => void;
}) {
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [reading, setReading] = useState(false);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    if (!report?.rows.length) onIds(null);
  }, [report]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (f: File) => {
    setError(null);
    setReading(true);
    try {
      setReport(await readReport(f));
    } catch (e) {
      setReport(null);
      setError(e);
    } finally {
      setReading(false);
    }
  };
  const pickSheet = async (i: number) => {
    if (!report?.xlsx) return;
    setError(null);
    try {
      const r = await readXlsxSheet(report.xlsx.bytes, i);
      setReport({ ...report, rows: r.rows, xlsx: { ...report.xlsx, sheet: r.sheet } });
    } catch (e) {
      setError(e);
    }
  };
  const sheets = report?.xlsx?.sheets ?? [];

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) void open(f);
        }}
        className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[12px] border border-dashed p-3 transition ${dragging ? 'border-ink bg-canvas' : 'border-ink/25 bg-paper'}`}
      >
        <label
          className="btn-ghost h-10 shrink-0 cursor-pointer bg-paper px-3.5 py-0 text-[14px] has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-coin"
          aria-busy={reading || undefined}
        >
          <FileSpreadsheet size={16} strokeWidth={1.75} aria-hidden="true" />
          {reading ? 'Reading…' : report ? 'Choose another file' : 'Choose a file'}
          <input
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="sr-only"
            disabled={reading}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void open(f);
            }}
          />
        </label>
        <span className="min-w-0 flex-1 text-[13px] leading-snug text-stone">
          {report ? (
            <>
              <b className="font-semibold text-ink">{report.name}</b> ·{' '}
              {plural(report.rows.length, 'row')}
            </>
          ) : (
            'or drop it here. CSV, TSV or Excel (.xlsx), up to 10 MB. Read in this browser; the file isn’t uploaded.'
          )}
        </span>
        {report && (
          <button
            type="button"
            onClick={() => setReport(null)}
            className="grid h-9 w-9 place-items-center rounded-full text-stone transition hover:bg-canvas hover:text-ink"
            aria-label="Remove the file"
          >
            <X size={16} />
          </button>
        )}
      </div>
      <ErrorNote error={error} />
      {sheets.length > 1 && (
        <label className="flex items-center gap-2 text-[13px] font-semibold text-graphite">
          Sheet
          <select
            className="input h-9 w-auto py-0 text-[13px]"
            value={report!.xlsx!.sheet}
            onChange={(e) => void pickSheet(Number(e.target.value))}
          >
            {sheets.map((s, i) => (
              <option key={i} value={i}>
                {s.name}
                {s.hidden ? ' (hidden)' : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {report && report.rows.length === 0 && (
        <p className="text-[14px] text-graphite">
          {sheets.length > 1 ? 'This sheet is empty.' : 'This file has no rows.'}
        </p>
      )}
      {report && report.rows.length > 0 && (
        <ColumnPicker rows={report.rows} providerId={providerId} onIds={onIds} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Take back: preview */

type PreviewFilter = 'all' | 'take' | 'reversed' | 'waiting' | 'not_found';

/**
 * A fifth or more of the list isn't this network's: the wrong network or the wrong column is
 * likelier than that many bad ids, so applying asks for a second look.
 */
const manyUnknown = (t: Preview['totals']) => t.notFound > 0 && t.notFound >= t.ids * 0.2;

function PreviewView({ preview, network }: { preview: Preview; network: string }) {
  const t = preview.totals;
  const [filter, setFilter] = useState<PreviewFilter>(t.toReverse ? 'take' : 'all');
  const [all, setAll] = useState(false);
  const shown = preview.items.filter((i) =>
    filter === 'all'
      ? true
      : filter === 'take'
        ? i.state === 'credited' || i.state === 'held'
        : i.state === filter,
  );
  const users = usersIn(preview);
  return (
    <section aria-labelledby="preview-title" className="card overflow-hidden">
      <div className="flex min-h-[52px] flex-wrap items-center gap-3 border-b border-hairline px-4 py-3 sm:px-5">
        <h2 id="preview-title" className="text-[15px] font-bold tracking-[-0.01em]">
          2 · What would happen
        </h2>
        <span className="text-[13px] text-stone">Preview only: nothing has changed yet.</span>
      </div>
      {manyUnknown(t) && (
        <div
          role="alert"
          className="flex items-start gap-2.5 border-b border-hairline bg-berry/[0.06] px-4 py-4 sm:px-5"
        >
          <TriangleAlert size={18} className="mt-0.5 shrink-0 text-berry-deep" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-berry-deep">
              {fmtNum(t.notFound)} of {plural(t.ids, 'id')} (
              {Math.round((t.notFound / t.ids) * 100)}
              %) aren’t {network} transactions
            </h3>
            <p className="mt-1 max-w-3xl text-[14px] leading-relaxed text-graphite">
              That usually means the wrong network or the wrong column (a user, offer or click id
              instead of the transaction id). Numeric ids can match someone else’s conversions, so
              check both before you apply.
            </p>
          </div>
        </div>
      )}
      {/* Hairline dividers in both layouts: the gaps show the border colour behind the cells. */}
      <div className="grid grid-cols-2 gap-px border-b border-hairline bg-hairline md:grid-cols-4">
        <Figure label="Taken back">
          <Minus coins={t.coins} />
          <Sub>
            {plural(t.toReverse, 'reward')} · {plural(users, 'user')} · {cents(t.revenueMinor)}{' '}
            revenue
            {t.waiting > 0 && (
              <>
                {' '}
                · {fmtNum(t.waiting)} more as{' '}
                {t.waiting === 1 ? 'its credit lands' : 'their credits land'}
              </>
            )}
          </Sub>
        </Figure>
        <Figure label="Still on hold">
          {fmtNum(t.held)}
          <Sub>{t.held ? 'Their holds are cancelled' : 'None'}</Sub>
        </Figure>
        <Figure label="Referral commissions">
          {t.commissionCoins ? <Minus coins={t.commissionCoins} /> : '0'}
          <Sub>{t.commissionCoins ? 'Taken back from referrers' : 'None to take back'}</Sub>
        </Figure>
        <Figure label="Skipped">
          {fmtNum(t.alreadyReversed + t.notFound)}
          <Sub>
            {fmtNum(t.alreadyReversed)} already taken back · {fmtNum(t.notFound)} not found
          </Sub>
        </Figure>
      </div>
      {preview.shortUsers.length > 0 && <ShortUsers users={preview.shortUsers} />}
      <div className="flex flex-wrap items-center gap-3 px-4 pb-1 pt-4 sm:px-5">
        <Tabs
          label="Show"
          value={filter}
          onChange={setFilter}
          items={[
            { key: 'take', label: 'Taken back', count: t.toReverse },
            ...(t.waiting
              ? [{ key: 'waiting' as const, label: 'Credit on its way', count: t.waiting }]
              : []),
            { key: 'reversed', label: 'Already taken back', count: t.alreadyReversed },
            { key: 'not_found', label: 'Not found', count: t.notFound, hot: manyUnknown(t) },
            { key: 'all', label: 'All', count: t.ids },
          ]}
        />
      </div>
      {shown.length === 0 ? (
        <EmptyState title="Nothing here">No ids in this list are in this state.</EmptyState>
      ) : (
        <ScrollX>
          <table className="data-table table-compact min-w-[860px]">
            <thead>
              <tr>
                <th>Transaction</th>
                <th>User</th>
                <th>Offer</th>
                <th className="text-right">Coins</th>
                <th>Earned</th>
                <th>What happens</th>
              </tr>
            </thead>
            <tbody>
              {(all ? shown : shown.slice(0, 100)).map((i) => (
                <tr key={i.txid}>
                  <td className="max-w-[200px]">
                    <code className="block truncate text-[12px] text-graphite" title={i.txid}>
                      {i.txid}
                    </code>
                  </td>
                  <td>
                    {i.userId ? (
                      <UserChip
                        id={i.userId}
                        username={i.username}
                        deleted={i.userStatus === 'deleted'}
                      />
                    ) : (
                      <span className="text-stone">—</span>
                    )}
                  </td>
                  <td className="max-w-[220px] truncate" title={i.offerName ?? undefined}>
                    {i.offerName ?? <span className="text-stone">—</span>}
                  </td>
                  <td className="text-right font-semibold">{i.coins ? fmtNum(i.coins) : '—'}</td>
                  <td className="whitespace-nowrap text-graphite">{day(i.createdAt)}</td>
                  <td className="min-w-[260px]">
                    <WhatHappens item={i} network={network} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      )}
      {!all && shown.length > 100 && (
        <div className="border-t border-hairline px-4 py-3 sm:px-5">
          <button
            type="button"
            className="btn-ghost h-9 px-3.5 py-0 text-[13px]"
            onClick={() => setAll(true)}
          >
            Show all {fmtNum(shown.length)}
          </button>
        </div>
      )}
    </section>
  );
}

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 bg-paper px-4 py-4 sm:px-5">
      <div className="text-[13px] font-semibold text-stone">{label}</div>
      <div className="mt-1.5 text-[24px] font-extrabold leading-none tracking-[-0.025em] tabular-nums sm:text-[28px]">
        {children}
      </div>
    </div>
  );
}
function Sub({ children }: { children: ReactNode }) {
  return (
    <div className="mt-2 text-[13px] font-normal leading-snug tracking-normal text-graphite">
      {children}
    </div>
  );
}

function WhatHappens({ item: i, network }: { item: PreviewItem; network: string }) {
  if (i.state === 'waiting')
    return (
      <span className="text-graphite">
        <Pill tone="sun">Waits</Pill> The credit is in but not processed yet: taken back the moment
        it is
      </span>
    );
  if (i.state === 'not_found')
    return (
      <span className="text-graphite">
        <Pill>Skipped</Pill> No {network} conversion with this id
      </span>
    );
  if (i.state === 'reversed')
    return (
      <span className="text-graphite">
        <Pill>Skipped</Pill> Already taken back{' '}
        {i.reversedBy === 'staff' ? 'by staff' : i.reversedBy === 'network' ? 'by the network' : ''}
        {i.reversedAt ? `, ${day(i.reversedAt)}` : ''}
      </span>
    );
  return (
    <span className="block leading-snug">
      {i.state === 'held' ? (
        <>
          <StatusPill status="held" /> Hold cancelled
        </>
      ) : (
        <>
          <StatusPill status="credited" /> Taken from the balance
        </>
      )}
      {i.commission && (
        <span className="mt-1 block text-[13px] text-graphite">
          Also {fmtNum(i.commission.coins)} commission from{' '}
          {i.commission.username ?? 'the referrer'}
          {i.commissionShortfall > 0 && (
            <b className="font-semibold text-berry-deep">
              {' '}
              · {fmtNum(i.commissionShortfall)} short
            </b>
          )}
        </span>
      )}
      {i.userStatus === 'deleted' && (
        <span className="mt-1 block text-[13px] text-graphite">
          Account deleted: comes out of the coins it gave up
        </span>
      )}
      {i.shortfall > 0 && (
        <span className="mt-1 block text-[13px] font-semibold text-berry-deep">
          {fmtNum(i.shortfall)} coins already spent
        </span>
      )}
    </span>
  );
}

function ShortUsers({ users }: { users: ShortUser[] }) {
  const flagged = users.filter((u) => u.flagged);
  const held = flagged.reduce((n, u) => n + u.unsentCashouts, 0);
  return (
    <div className="border-b border-hairline bg-berry/[0.04] px-4 py-4 sm:px-5">
      <div className="flex items-start gap-2.5">
        <TriangleAlert size={18} className="mt-0.5 shrink-0 text-berry-deep" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <h3 className="text-[15px] font-bold text-berry-deep">
            {plural(users.length, 'user has', 'users have')} already spent some of these coins
          </h3>
          <p className="mt-1 max-w-3xl text-[14px] leading-relaxed text-graphite">
            A balance never goes below zero. What they can’t cover is booked as a loss, as when a
            network sends the reversal: the earner gets a high “reversal after spend” flag
            {held ? (
              <>
                {' '}
                and {plural(held, 'cash-out')} not sent yet {held === 1 ? 'goes' : 'go'} on hold
              </>
            ) : null}
            . Coins already reserved for a cash-out in progress are never touched. Referrers who
            spent a commission aren’t flagged.
          </p>
        </div>
      </div>
      <ul className="mt-3 divide-y divide-hairline overflow-hidden rounded-[12px] border border-hairline bg-paper">
        {users.slice(0, 50).map((u) => (
          <li
            key={u.userId}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 px-3.5 py-2.5 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
          >
            <UserChip
              id={u.userId}
              username={u.username}
              deleted={u.status === 'deleted'}
              sub={`Has ${fmtNum(u.available)} available, ${fmtNum(u.pending)} pending${u.withdrawing ? `, ${fmtNum(u.withdrawing)} cashing out` : ''}`}
            />
            <span className="text-right text-[13px] tabular-nums text-graphite">
              takes {fmtNum(u.takeBack)} ·{' '}
              <b className="font-bold text-berry-deep">{fmtNum(u.short)} short</b>
            </span>
            <span className="col-span-2 flex flex-wrap gap-1.5 sm:col-span-1 sm:justify-end">
              {u.flagged ? <Pill tone="berry">Flagged</Pill> : <Pill>Referrer</Pill>}
              {u.unsentCashouts > 0 && (
                <Pill tone="coin">{plural(u.unsentCashouts, 'cash-out')} on hold</Pill>
              )}
            </span>
          </li>
        ))}
      </ul>
      {users.length > 50 && (
        <p className="mt-2 text-[13px] text-stone">and {fmtNum(users.length - 50)} more.</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Take back: apply */

function ApplyPanel({
  preview,
  network,
  onStarted,
}: {
  preview: Preview;
  network: string;
  onStarted: (b: Batch) => void;
}) {
  const ids = useId();
  const [reason, setReason] = useState('');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  // One key per previewed list: a second click, or a retry after a dropped answer, finds the
  // batch the first one recorded instead of starting another.
  const requestKey = useMemo(() => crypto.randomUUID(), [preview]);
  const t = preview.totals;
  const risky = manyUnknown(t);
  const ok = reason.trim().length >= 3 && (!risky || checked) && !busy;
  const short = preview.shortUsers.filter((u) => u.flagged);
  const n = t.toReverse + t.waiting;
  const apply = async () => {
    if (busy) return;
    const yes = await confirmAction({
      title: `Take back ${plural(n, 'reward')} from ${network}?`,
      body: (
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <b className="text-ink">−{fmtNum(t.coins)} coins</b> from{' '}
            {plural(usersIn(preview), 'user')} ({cents(t.revenueMinor)} revenue)
            {t.held ? `, ${fmtNum(t.held)} of them still on hold` : ''}.
          </li>
          {t.waiting > 0 && (
            <li>
              {plural(t.waiting, 'reward')} more as{' '}
              {t.waiting === 1 ? 'its credit lands' : 'their credits land'}.
            </li>
          )}
          {t.commissionCoins > 0 && (
            <li>−{fmtNum(t.commissionCoins)} coins of referral commission.</li>
          )}
          {short.length > 0 && (
            <li>
              {plural(short.length, 'user')} {short.length === 1 ? 'is' : 'are'} short and{' '}
              {short.length === 1 ? 'gets' : 'get'} flagged.
            </li>
          )}
          {t.alreadyReversed + t.notFound > 0 && (
            <li>
              {plural(t.alreadyReversed + t.notFound, 'id')} skipped
              {risky ? `, ${fmtNum(t.notFound)} of them not ${network} transactions` : ''}.
            </li>
          )}
          <li>This can’t be undone.</li>
        </ul>
      ),
      confirmLabel: `Take back ${fmtNum(t.coins)} coins`,
      tone: 'danger',
    });
    if (yes === null) return;
    setBusy(true);
    const r = await run(() =>
      api<{ batch: Batch }>('/reversals', {
        body: {
          providerId: preview.providerId,
          txids: preview.items.map((i) => i.txid),
          reason: reason.trim(),
          requestKey,
        },
      }),
    );
    setBusy(false);
    if (r) onStarted(r.batch);
  };
  return (
    <Card title="3 · Apply">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div>
          <label className="label" htmlFor={`${ids}-why`}>
            Reason <span className="font-normal text-stone">(staff only)</span>
          </label>
          <textarea
            id={`${ids}-why`}
            className="input min-h-[88px] resize-y text-[14px] leading-relaxed"
            placeholder="e.g. Rejected leads in the TimeWall dashboard, 1–20 Oct (VPN)"
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="mt-1.5 text-[12px] leading-snug text-stone">
            Kept in the history and the audit log. Users don’t see it: their wallet shows “Taken
            back by the partner”, the same as a network reversal.
          </p>
          {risky && (
            <label className="mt-3 flex items-start gap-2.5 rounded-[10px] border border-berry/30 bg-berry/[0.04] px-3 py-2.5 text-[13px] font-semibold leading-snug text-berry-deep">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 accent-ink"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />
              I’ve checked the network and the column: {fmtNum(t.notFound)} of these ids aren’t{' '}
              {network} transactions, and the rest are the ones it rejected.
            </label>
          )}
        </div>
        <button
          type="button"
          className="btn-primary !bg-berry-deep hover:!bg-[#a82015]"
          disabled={!ok}
          aria-busy={busy || undefined}
          onClick={apply}
        >
          <Undo2 size={16} aria-hidden="true" /> Take back {plural(n, 'reward')}
        </button>
      </div>
    </Card>
  );
}

/** Ids of a batch that have an outcome (waiting ones too: they finish on their own). */
const handledIn = (b: Batch) => b.reversed + b.alreadyReversed + b.waiting + b.notFound + b.failed;

/**
 * Applies a batch a chunk per request until it's done, showing progress. When the server stopped
 * a chunk early because the database was busy, it waits a little and carries on; after a few such
 * chunks in a row with no progress, or any failed request, it stops, and "Continue" picks up where
 * it stopped (done items are never redone).
 */
function Progress({
  batch,
  onChange,
  done: whenDone,
}: {
  batch: Batch;
  onChange: (b: Batch) => void;
  /** Actions once it's finished. */
  done?: ReactNode;
}) {
  const [error, setError] = useState<unknown>(null);
  const [running, setRunning] = useState(false);
  const started = useRef<string | null>(null);
  const qc = useQueryClient();
  const go = async () => {
    setError(null);
    setRunning(true);
    try {
      let b = batch;
      let stuck = 0;
      while (b.status !== 'done') {
        const before = handledIn(b);
        const r = await api<{ batch: Batch; stalled: string | null }>(`/reversals/${b.id}/apply`, {
          body: {},
        });
        b = r.batch;
        onChange({ ...batch, ...b });
        if (r.stalled && handledIn(b) === before) {
          if (++stuck >= 4)
            throw new Error(`The database stayed busy (${r.stalled}). Try again in a minute.`);
          await new Promise((res) => setTimeout(res, 1500 * stuck));
        } else stuck = 0;
      }
      void qc.invalidateQueries({ queryKey: ['reversals'] });
    } catch (e) {
      setError(e);
    } finally {
      setRunning(false);
    }
  };
  // Starts on its own when the batch was just created or reopened unfinished (once: StrictMode
  // runs effects twice, and a second loop would only find the batch busy).
  useEffect(() => {
    if (batch.status !== 'applying' || started.current === batch.id) return;
    started.current = batch.id;
    void go();
  }, [batch.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Leaving mid-way only pauses it, but say so.
  useEffect(() => {
    if (!running) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [running]);

  const handled = handledIn(batch);
  const pct = batch.total ? Math.round((handled / batch.total) * 100) : 100;
  const done = batch.status === 'done';
  return (
    <Card title={done ? `Done: ${plural(batch.reversed, 'reward')} taken back` : 'Taking back…'}>
      <div className="space-y-4">
        <div>
          <div className="flex items-baseline justify-between gap-3 text-[14px]">
            <span className="font-semibold" aria-live="polite">
              {fmtNum(handled)} of {plural(batch.total, 'id')}
            </span>
            <span className="tabular-nums text-stone">{pct}%</span>
          </div>
          <div
            className="mt-2 h-2 overflow-hidden rounded-full bg-ink/[0.08]"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={batch.total}
            aria-valuenow={handled}
            aria-label="Ids handled"
          >
            <div
              className="h-full rounded-full bg-ink transition-[width] duration-200"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
        <Outcomes b={batch} />
        {!done && !running && !error && (
          <p className="text-[14px] text-graphite">Paused. Continue to apply the rest.</p>
        )}
        {error ? (
          <ErrorNote
            error={
              error instanceof ApiError && error.status === 409
                ? error
                : new Error(
                    `${(error as Error).message} Nothing is lost: what’s done stays done. Continue to apply the rest.`,
                  )
            }
          />
        ) : null}
        <div className="flex flex-wrap gap-2">
          {!done && !running && (
            <button type="button" className="btn-primary" onClick={go}>
              Continue
            </button>
          )}
          {done && whenDone}
        </div>
        {!done && (
          <p className="text-[12px] text-stone">
            Keep this page open until it finishes. If you leave, it pauses; continue it from
            History.
          </p>
        )}
      </div>
    </Card>
  );
}

function Outcomes({ b }: { b: Batch }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13px] sm:grid-cols-4">
      <Fact label="Taken back">
        <Minus coins={b.coins} /> <span className="text-stone">· {fmtNum(b.reversed)}</span>
      </Fact>
      <Fact label="Already taken back">{fmtNum(b.alreadyReversed)}</Fact>
      <Fact label="Not found">
        {fmtNum(b.notFound)}
        {b.waiting > 0 && (
          <span className="block text-[12px] font-semibold text-graphite">
            {fmtNum(b.waiting)} waiting for {b.waiting === 1 ? 'its credit' : 'their credits'}
          </span>
        )}
      </Fact>
      <Fact label="Failed">
        <span className={b.failed ? 'text-berry-deep' : ''}>{fmtNum(b.failed)}</span>
      </Fact>
    </dl>
  );
}
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[12px] font-semibold text-stone">{label}</dt>
      <dd className="mt-0.5 text-[16px] font-bold tabular-nums">{children}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ History */

function HistoryList({ query, canApply }: { query: HistoryQuery; canApply: boolean }) {
  const [params, setParams] = useSearchParams();
  const openId = params.get('batch');
  const batches = (query.data?.pages ?? []).flatMap((p) => p.batches);
  const setOpen = (id: string | null) =>
    setParams((p) => (id ? p.set('batch', id) : p.delete('batch'), p), { replace: true });
  return (
    <>
      <ErrorNote error={query.error} onRetry={() => void query.refetch()} />
      {!(query.error && !query.data) && (
        <div className="card overflow-hidden">
          {query.isLoading ? (
            <SkeletonRows />
          ) : batches.length === 0 ? (
            <EmptyState icon={HistoryIcon} title="No reversals yet">
              Every batch staff apply shows up here, with who ran it, why, and what happened to each
              id.
            </EmptyState>
          ) : (
            <ul className="divide-y divide-hairline">
              {batches.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => setOpen(b.id)}
                    className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 px-4 py-3.5 text-left transition hover:bg-[#fbf8f3] focus-visible:-outline-offset-2 sm:px-5"
                  >
                    <span className="min-w-0">
                      <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                        <b className="font-bold">{b.network ?? b.providerId}</b>
                        <BatchPill b={b} />
                      </span>
                      <span
                        className="mt-1 block truncate text-[14px] text-graphite"
                        title={b.reason}
                      >
                        {b.reason}
                      </span>
                      <span className="mt-0.5 block text-[12px] text-stone">
                        {b.staffName ?? 'Staff'} · <TimeAgo t={b.createdAt} /> ·{' '}
                        {plural(b.total, 'id')}
                      </span>
                    </span>
                    <span className="text-right">
                      <Minus coins={b.coins} className="text-[16px]" />
                      <span className="block text-[12px] tabular-nums text-stone">
                        {fmtNum(b.reversed)} taken back
                        {b.alreadyReversed ? ` · ${fmtNum(b.alreadyReversed)} already` : ''}
                        {b.waiting ? ` · ${fmtNum(b.waiting)} waiting` : ''}
                        {b.notFound ? ` · ${fmtNum(b.notFound)} not found` : ''}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {query.hasNextPage && (
            <div className="border-t border-hairline px-4 py-3 sm:px-5">
              <button
                type="button"
                className="btn-ghost h-9 px-3.5 py-0 text-[13px]"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
              >
                {query.isFetchingNextPage ? 'Loading…' : 'Show older'}
              </button>
            </div>
          )}
        </div>
      )}
      {openId && <BatchDrawer id={openId} canApply={canApply} onClose={() => setOpen(null)} />}
    </>
  );
}

function BatchPill({ b }: { b: Batch }) {
  if (b.status === 'applying') return <Pill tone="coin">Unfinished</Pill>;
  if (b.failed) return <Pill tone="berry">{plural(b.failed, 'failure')}</Pill>;
  if (b.waiting) return <Pill tone="sun">{fmtNum(b.waiting)} waiting for credit</Pill>;
  return <Pill tone="neutral">Done</Pill>;
}

function summaryRows(b: Batch): [ReactNode, ReactNode][] {
  const rows: [ReactNode, ReactNode][] = [
    ['Reason', <span className="whitespace-pre-wrap font-normal">{b.reason}</span>],
    [
      'Taken back',
      <>
        <Minus coins={b.coins} /> coins from {plural(b.reversed, 'reward')} ({cents(b.revenueMinor)}{' '}
        revenue)
      </>,
    ],
  ];
  if (b.commissionCoins) rows.push(['Commissions', <Minus coins={b.commissionCoins} />]);
  if (b.shortfallCoins)
    rows.push([
      'Already spent',
      <span className="text-berry-deep">{fmtNum(b.shortfallCoins)} coins, booked as a loss</span>,
    ]);
  if (b.waiting)
    rows.push([
      'Waiting',
      `${plural(b.waiting, 'id')} whose credit is in but not processed yet: taken back when it is`,
    ]);
  rows.push([
    'Skipped',
    `${fmtNum(b.alreadyReversed)} already taken back · ${fmtNum(b.notFound)} not found`,
  ]);
  if (b.failed) rows.push(['Failed', <span className="text-berry-deep">{fmtNum(b.failed)}</span>]);
  rows.push(['Batch', <Mono>{b.id}</Mono>]);
  return rows;
}

const OUTCOME: Record<
  BatchItem['outcome'],
  { label: string; tone: 'neutral' | 'berry' | 'coin' | 'sun' }
> = {
  reversed: { label: 'Taken back', tone: 'berry' },
  already_reversed: { label: 'Already taken back', tone: 'neutral' },
  waiting: { label: 'Waiting for credit', tone: 'sun' },
  not_found: { label: 'Not found', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'berry' },
  pending: { label: 'Not applied yet', tone: 'coin' },
};

/** "Retry 2 failed ids", "Check 3 waiting ids again", or both at once. */
function retryLabel(b: Batch) {
  if (b.failed && b.waiting) return `Try ${plural(b.failed + b.waiting, 'id')} again`;
  if (b.failed) return `Retry ${plural(b.failed, 'failed id')}`;
  return `Check ${plural(b.waiting, 'waiting id')} again`;
}

function BatchDrawer({
  id,
  canApply,
  onClose,
}: {
  id: string;
  canApply: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['reversals', id],
    queryFn: () =>
      api<{ batch: Batch; items: BatchItem[]; staffNames: Record<string, string> }>(
        `/reversals/${id}`,
      ),
  });
  const [applying, setApplying] = useState<Batch | null>(null);
  const [filter, setFilter] = useState<'all' | BatchItem['outcome']>('all');
  const b = data?.batch;
  const items = data?.items ?? [];
  const shown = filter === 'all' ? items : items.filter((i) => i.outcome === filter);
  const count = (o: BatchItem['outcome']) => items.filter((i) => i.outcome === o).length;
  const refresh = () => {
    void refetch();
    void qc.invalidateQueries({ queryKey: ['reversals'] });
  };
  const retry = async () => {
    const r = await run(() => api<{ retried: number }>(`/reversals/${id}/retry`, { body: {} }));
    if (r && b) setApplying({ ...b, status: 'applying' });
  };
  return (
    <Drawer
      onClose={onClose}
      title={b ? `${b.network ?? b.providerId} reversal` : 'Reversal'}
      subtitle={b ? `${b.staffName ?? 'Staff'} · ${new Date(b.createdAt).toLocaleString()}` : null}
      headerRight={b && <BatchPill b={b} />}
      footer={
        b && canApply && !applying && (b.status === 'applying' || b.failed + b.waiting > 0) ? (
          <div className="flex flex-wrap justify-end gap-2">
            {b.status === 'applying' ? (
              <button type="button" className="btn-primary" onClick={() => setApplying(b)}>
                Continue this batch
              </button>
            ) : (
              <button type="button" className="btn-primary" onClick={retry}>
                {retryLabel(b)}
              </button>
            )}
          </div>
        ) : null
      }
    >
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {isLoading && <SkeletonRows rows={4} />}
      {b && (
        <div className="space-y-5">
          {applying ? (
            <Progress
              batch={applying}
              onChange={(n) => {
                setApplying(n);
                if (n.status === 'done') {
                  setApplying(null);
                  refresh();
                }
              }}
            />
          ) : (
            <div className="card p-4 sm:p-5">
              <KV items={summaryRows(b)} />
            </div>
          )}
          <div>
            <Tabs
              label="Show"
              value={filter}
              onChange={setFilter}
              items={[
                { key: 'all', label: 'All', count: items.length },
                { key: 'reversed', label: 'Taken back', count: count('reversed') },
                { key: 'already_reversed', label: 'Already', count: count('already_reversed') },
                ...(count('waiting')
                  ? [{ key: 'waiting' as const, label: 'Waiting', count: count('waiting') }]
                  : []),
                { key: 'not_found', label: 'Not found', count: count('not_found') },
                ...(count('failed')
                  ? [{ key: 'failed' as const, label: 'Failed', count: count('failed'), hot: true }]
                  : []),
                ...(count('pending')
                  ? [{ key: 'pending' as const, label: 'Not applied', count: count('pending') }]
                  : []),
              ]}
            />
            <ul className="card mt-3 divide-y divide-hairline overflow-hidden">
              {shown.slice(0, 300).map((i) => (
                <li
                  key={i.txid}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 px-4 py-3"
                >
                  <span className="min-w-0">
                    <code className="block truncate text-[12px] text-graphite" title={i.txid}>
                      {i.txid}
                    </code>
                    {i.userId ? (
                      <span className="mt-1.5 block">
                        <UserChip
                          id={i.userId}
                          username={i.username}
                          deleted={i.userStatus === 'deleted'}
                          sub={i.offerName ?? undefined}
                        />
                      </span>
                    ) : null}
                    {(() => {
                      const notes = [
                        i.wasHeld ? 'Hold cancelled' : '',
                        i.commissionCoins
                          ? `${fmtNum(i.commissionCoins)} commission taken back`
                          : '',
                        i.shortfall ? `${fmtNum(i.shortfall)} coins already spent` : '',
                        i.note ?? '',
                        // Someone carried on another's batch: say whose request did this one.
                        i.actor && i.actor !== b.staffId
                          ? `by ${data?.staffNames[i.actor] ?? 'another staff member'}`
                          : '',
                      ].filter(Boolean);
                      return notes.length ? (
                        <span className="mt-1.5 block text-[13px] leading-snug text-graphite">
                          {notes.join(' · ')}
                        </span>
                      ) : null;
                    })()}
                  </span>
                  <span className="flex flex-col items-end gap-1">
                    <Pill tone={OUTCOME[i.outcome].tone}>{OUTCOME[i.outcome].label}</Pill>
                    {i.outcome === 'reversed' && i.coins ? (
                      <Minus coins={i.coins} className="text-[14px]" />
                    ) : i.conversionCoins ? (
                      <span className="text-[13px] tabular-nums text-stone">
                        {fmtNum(i.conversionCoins)}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
              {shown.length === 0 && (
                <li className="px-4 py-6 text-center text-[14px] text-stone">Nothing here.</li>
              )}
            </ul>
            {shown.length > 300 && (
              <p className="mt-2 text-[13px] text-stone">
                Showing the first 300 of {fmtNum(shown.length)}. Filter to see the rest.
              </p>
            )}
          </div>
        </div>
      )}
    </Drawer>
  );
}
